import { useEffect } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import { Button, Stepper, ToggleSwitch } from '@chillout/ui'
import { AdminScreen } from '../AdminScreen'
import { Card } from '../../shared/Card'
import { SectionErrorState } from '../shared/SectionErrorState'
import { SettingsSaveConfirmModal } from './SettingsSaveConfirmModal'
import { useAppSettings } from './useAppSettings'

const MAX_STAMPS = 999

function SettingRow({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <View className="flex-row items-center justify-between gap-4 py-4">
      <View className="min-w-0 flex-1">
        <Text className="text-sm font-semibold text-primary">{title}</Text>
        <Text className="mt-0.5 text-xs font-normal text-secondary">{description}</Text>
      </View>
      <View className="shrink-0" style={{ minWidth: 200 }}>{children}</View>
    </View>
  )
}

function SettingsSkeleton() {
  return (
    <Card variant="dashboard" className="p-6">
      <View className="gap-2">
        <View className="h-5 w-40 animate-pulse rounded bg-canvas-primary" />
        <View className="h-4 w-64 animate-pulse rounded bg-canvas-primary" />
      </View>
      <View className="mt-6 gap-6">
        <View className="h-10 animate-pulse rounded bg-canvas-primary" />
        <View className="h-10 animate-pulse rounded bg-canvas-primary" />
        <View className="h-10 animate-pulse rounded bg-canvas-primary" />
      </View>
    </Card>
  )
}

interface SettingsScreenProps {
  onDirtyChange: (dirty: boolean) => void
}

export function SettingsScreen({ onDirtyChange }: SettingsScreenProps) {
  const { t } = useTranslation()
  const settings = useAppSettings()

  useEffect(() => {
    onDirtyChange(settings.hasChanges)
  }, [settings.hasChanges, onDirtyChange])

  return (
    <AdminScreen
      title={t('adminDashboard.settingsTitle')}
      subtitle={t('adminDashboard.settingsSubtitle')}
      actions={
        <Button
          label={t('settings.saveChanges')}
          fullWidth={false}
          disabled={!settings.hasChanges}
          onPress={settings.requestSave}
        />
      }
    >
      {settings.isError ? (
        <SectionErrorState
          message={t('common.error')}
          retryLabel={t('common.retry')}
          onRetry={settings.onRetry}
        />
      ) : settings.isLoading ? (
        <SettingsSkeleton />
      ) : (
        <Card variant="dashboard" className="p-6">
          <View>
            <Text className="text-lg font-semibold text-primary">
              {t('settings.programOptions.title')}
            </Text>
            <Text className="mt-1 text-sm font-normal text-secondary">
              {t('settings.programOptions.subtitle')}
            </Text>
          </View>

          <View className="mt-4">
            <SettingRow
              title={t('settings.maxStampsPerTransaction.title')}
              description={t('settings.maxStampsPerTransaction.description')}
            >
              <Controller
                control={settings.control}
                name="maxStamps"
                render={({ field }) => (
                  <Stepper
                    value={field.value}
                    onChange={field.onChange}
                    min={1}
                    max={MAX_STAMPS}
                    countLabel={String(field.value)}
                    decrementAccessibilityLabel={t('settings.maxStampsPerTransaction.decrementAccessibilityLabel')}
                    incrementAccessibilityLabel={t('settings.maxStampsPerTransaction.incrementAccessibilityLabel')}
                  />
                )}
              />
            </SettingRow>

            <View className="border-t border-border">
              <SettingRow
                title={t('settings.receiptReference.title')}
                description={t('settings.receiptReference.description')}
              >
                <View className="items-end">
                  <Controller
                    control={settings.control}
                    name="receiptReferenceEnabled"
                    render={({ field }) => (
                      <ToggleSwitch
                        value={field.value}
                        onValueChange={field.onChange}
                        accessibilityLabel={t('settings.receiptReference.accessibilityLabel')}
                      />
                    )}
                  />
                </View>
              </SettingRow>
            </View>

            <View className="border-t border-border">
              <SettingRow
                title={t('settings.referralProgram.title')}
                description={t('settings.referralProgram.description')}
              >
                <View className="items-end">
                  <Controller
                    control={settings.control}
                    name="referralProgramEnabled"
                    render={({ field }) => (
                      <ToggleSwitch
                        value={field.value}
                        onValueChange={field.onChange}
                        accessibilityLabel={t('settings.referralProgram.accessibilityLabel')}
                      />
                    )}
                  />
                </View>
              </SettingRow>
            </View>
          </View>

          <View className="mt-8">
            <Text className="text-lg font-semibold text-primary">
              {t('settings.cashierPermissions.title')}
            </Text>
            <Text className="mt-1 text-sm font-normal text-secondary">
              {t('settings.cashierPermissions.subtitle')}
            </Text>
          </View>

          <View className="mt-4">
            <SettingRow
              title={t('settings.cashierStampCorrection.title')}
              description={t('settings.cashierStampCorrection.description')}
            >
              <View className="items-end">
                <Controller
                  control={settings.control}
                  name="cashierStampCorrectionEnabled"
                  render={({ field }) => (
                    <ToggleSwitch
                      value={field.value}
                      onValueChange={field.onChange}
                      accessibilityLabel={t('settings.cashierStampCorrection.accessibilityLabel')}
                    />
                  )}
                />
              </View>
            </SettingRow>

            <View className="border-t border-border">
              <SettingRow
                title={t('settings.cashierRewardRestore.title')}
                description={t('settings.cashierRewardRestore.description')}
              >
                <View className="items-end">
                  <Controller
                    control={settings.control}
                    name="cashierRewardRestoreEnabled"
                    render={({ field }) => (
                      <ToggleSwitch
                        value={field.value}
                        onValueChange={field.onChange}
                        accessibilityLabel={t('settings.cashierRewardRestore.accessibilityLabel')}
                      />
                    )}
                  />
                </View>
              </SettingRow>
            </View>
          </View>
        </Card>
      )}

      {settings.pendingSettings ? (
        <SettingsSaveConfirmModal
          visible
          savedSettings={settings.savedSettings}
          pendingSettings={settings.pendingSettings}
          saving={settings.isSaving}
          error={settings.saveError}
          onConfirm={settings.confirmSave}
          onCancel={settings.cancelSave}
        />
      ) : null}
    </AdminScreen>
  )
}